/**
 * DemoVideo — accessible product-demo player.
 *
 * - Muted, looping, `playsInline`, with native controls always available.
 * - Autoplays only while at least half visible, and pauses when scrolled away;
 *   never autoplays when the visitor prefers reduced motion (a hint explains
 *   why and the native play button works).
 * - A manual pause by the visitor is respected until the video leaves the view.
 * - Intrinsic width/height + poster => no layout shift; `preload="metadata"`.
 * - The text description is wired through `aria-describedby` and stands in for
 *   captions (the clips have no audio track).
 */

import { useEffect, useRef, useState } from 'react';

import { useT } from '@/i18n';
import {
  VIDEO_HEIGHT,
  VIDEO_WIDTH,
  videoPoster,
  videoSrc,
  type VideoId,
} from '@/routes/landing/landing-media';

function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function DemoVideo({
  id,
  title,
  description,
}: {
  id: VideoId;
  title: string;
  description: string;
}) {
  const { t } = useT();
  const ref = useRef<HTMLVideoElement>(null);
  const descId = `demo-video-desc-${id}`;
  const [reduced] = useState(prefersReducedMotion);
  const manualPause = useRef(false);
  const programmatic = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || reduced || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            if (!manualPause.current) {
              programmatic.current = false;
              el.play?.()?.catch(() => undefined);
            }
          } else {
            manualPause.current = false;
            programmatic.current = true;
            el.pause?.();
          }
        }
      },
      { threshold: [0, 0.5] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduced, id]);

  return (
    <figure className="m-0">
      <div className="overflow-hidden rounded-token-xl border border-border bg-black shadow-elevation-4">
        <video
          ref={ref}
          key={id}
          data-testid={`demo-video-${id}`}
          className="block h-auto w-full bg-black"
          width={VIDEO_WIDTH}
          height={VIDEO_HEIGHT}
          poster={videoPoster(id)}
          muted
          loop
          playsInline
          controls
          preload="metadata"
          aria-label={t('routes.landing.v2.tour.videoLabel', { title })}
          aria-describedby={descId}
          onPause={() => {
            if (!programmatic.current) manualPause.current = true;
          }}
        >
          <source src={videoSrc(id, 'mp4')} type="video/mp4" />
          <source src={videoSrc(id, 'webm')} type="video/webm" />
          {description}
        </video>
      </div>
      <figcaption id={descId} className="mt-3 text-sm leading-relaxed text-muted">
        {description}{' '}
        <span className="text-xs">
          {reduced ? t('routes.landing.v2.tour.reducedMotion') : t('routes.landing.v2.tour.noAudio')}
        </span>
      </figcaption>
    </figure>
  );
}
